/**
 * RFIDrawer Component
 * Create/Edit/View RFI (Request for Information) drawer
 * BuilderBel Construction Project Management System
 */

import { yupResolver } from '@hookform/resolvers/yup';
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  FileDown,
  FileText,
  MessageSquare,
  Send,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentProjectId } from '../../hooks/useProjectRedux';
import { useGetProjectTeamQuery } from '../../services/teamApi';
import {
  useCreateRFIMutation,
  useUpdateRFIMutation,
  useSubmitRFIMutation,
  useCloseRFIMutation,
  useVoidRFIMutation,
  downloadRFIPdf,
} from '../../services/rfiApi';
import type {
  RFI,
  RFIStatus,
  RFIPriority,
  RFIDiscipline,
  CreateRFIRequest,
  UpdateRFIRequest,
} from '../../types/rfi';
import {
  DatePicker,
  Dropdown,
  Input,
  SearchableDropdown,
  TextArea,
  CurrencyInput,
  NumberInput,
} from '../forms';
import RFICommentsSection from './RFICommentsSection';
import RFIResponseSection from './RFIResponseSection';
import RFIDistributionSection from './RFIDistributionSection';
import RFIReferencesSection from './RFIReferencesSection';

// Props interface
export interface RFIDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  rfi?: RFI | null;
  mode: 'create' | 'edit' | 'view';
  onSuccess?: () => void;
}

// Form data type
interface RFIFormData {
  subject: string;
  question: string;
  suggestedSolution: string;
  priority: RFIPriority;
  discipline: RFIDiscipline;
  dueDate: string;
  ballInCourt: string;
  ballInCourtName: string;
  assignedTo: string;
  assignedToName: string;
  location: string;
  drawingReference: string;
  specSection: string;
  hasCostImpact: boolean;
  estimatedCost: number;
  costDescription: string;
  hasScheduleImpact: boolean;
  daysImpacted: number;
  scheduleDescription: string;
}

// Status color mapping
const STATUS_COLORS: Record<RFIStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  open: 'bg-blue-100 text-blue-700',
  pending_response: 'bg-yellow-100 text-yellow-700',
  answered: 'bg-green-100 text-green-700',
  closed: 'bg-green-100 text-green-700',
  closed_void: 'bg-red-100 text-red-700',
};

// Status labels
const STATUS_LABELS: Record<RFIStatus, string> = {
  draft: 'Draft',
  open: 'Open',
  pending_response: 'Pending Response',
  answered: 'Answered',
  closed: 'Closed',
  closed_void: 'Void',
};

// Priority options
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// Discipline options
const DISCIPLINE_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'architectural', label: 'Architectural' },
  { value: 'structural', label: 'Structural' },
  { value: 'mechanical', label: 'Mechanical' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'civil', label: 'Civil' },
];

// Priority colors for badges
const PRIORITY_COLORS: Record<RFIPriority, string> = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

// Validation schema
const rfiSchema = Yup.object().shape({
  subject: Yup.string().required('Subject is required').max(200, 'Max 200 characters'),
  question: Yup.string().required('Question is required').max(5000, 'Max 5000 characters'),
  suggestedSolution: Yup.string().max(5000, 'Max 5000 characters'),
  priority: Yup.string().oneOf(['low', 'medium', 'high', 'critical']).required(),
  discipline: Yup.string()
    .oneOf([
      'general',
      'architectural',
      'structural',
      'mechanical',
      'electrical',
      'plumbing',
      'civil',
    ])
    .required(),
  dueDate: Yup.string().required('Due date is required'),
  ballInCourt: Yup.string().required('Ball-in-court is required'),
  ballInCourtName: Yup.string().required(),
  assignedTo: Yup.string(),
  assignedToName: Yup.string(),
  location: Yup.string().max(200, 'Max 200 characters'),
  drawingReference: Yup.string().max(200, 'Max 200 characters'),
  specSection: Yup.string().max(200, 'Max 200 characters'),
  hasCostImpact: Yup.boolean(),
  estimatedCost: Yup.number().min(0),
  costDescription: Yup.string().max(1000, 'Max 1000 characters'),
  hasScheduleImpact: Yup.boolean(),
  daysImpacted: Yup.number().min(0),
  scheduleDescription: Yup.string().max(1000, 'Max 1000 characters'),
});

// Section header component
interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  count?: number;
  isExpanded: boolean;
  onToggle: () => void;
  badge?: React.ReactNode;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  icon,
  title,
  count,
  isExpanded,
  onToggle,
  badge,
}) => (
  <button
    type="button"
    onClick={onToggle}
    className="w-full flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
  >
    <div className="flex items-center gap-2">
      {icon}
      <span className="text-sm font-medium text-gray-700">{title}</span>
      {count !== undefined && count > 0 && (
        <span className="px-1.5 py-0.5 text-xs bg-brand-100 text-brand-700 rounded-full">
          {count}
        </span>
      )}
      {badge}
    </div>
    {isExpanded ? (
      <ChevronDown className="h-4 w-4 text-gray-400" />
    ) : (
      <ChevronRight className="h-4 w-4 text-gray-400" />
    )}
  </button>
);

export const RFIDrawer: React.FC<RFIDrawerProps> = ({ isOpen, onClose, rfi, mode, onSuccess }) => {
  const projectId = useCurrentProjectId();
  const { success, error: showError } = useToast();

  // Mode flags
  const isCreateMode = mode === 'create';
  const isEditMode = mode === 'edit';
  const isViewMode = mode === 'view';

  // Collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    assignments: true,
    references: false,
    impact: false,
    distribution: false,
    response: false,
    comments: false,
  });

  // Modal states
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  // Fetch team members for dropdowns
  const { data: teamData } = useGetProjectTeamQuery(
    { projectId: projectId || '', limit: 100 },
    { skip: !projectId }
  );
  const teamMembers = useMemo(() => teamData?.data?.data || [], [teamData?.data?.data]);

  // Team member options for dropdowns
  const teamMemberOptions = useMemo(() => {
    return teamMembers.map(member => ({
      value: member.userId,
      label: member.user ? `${member.user.firstName} ${member.user.lastName}` : member.userId,
      description: member.role,
    }));
  }, [teamMembers]);

  // API mutations
  const [createRFI, { isLoading: isCreating }] = useCreateRFIMutation();
  const [updateRFI, { isLoading: isUpdating }] = useUpdateRFIMutation();
  const [submitRFI, { isLoading: isSubmitting }] = useSubmitRFIMutation();
  const [closeRFI, { isLoading: isClosing }] = useCloseRFIMutation();
  const [voidRFI, { isLoading: isVoiding }] = useVoidRFIMutation();

  const isSaving = isCreating || isUpdating;
  const isProcessing = isSaving || isSubmitting || isClosing || isVoiding;

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RFIFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(rfiSchema) as any,
    defaultValues: {
      subject: '',
      question: '',
      suggestedSolution: '',
      priority: 'medium',
      discipline: 'general',
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      ballInCourt: '',
      ballInCourtName: '',
      assignedTo: '',
      assignedToName: '',
      location: '',
      drawingReference: '',
      specSection: '',
      hasCostImpact: false,
      estimatedCost: 0,
      costDescription: '',
      hasScheduleImpact: false,
      daysImpacted: 0,
      scheduleDescription: '',
    },
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Watch values for impact sections
  const hasCostImpact = watch('hasCostImpact');
  const hasScheduleImpact = watch('hasScheduleImpact');

  // Reset form when RFI changes
  useEffect(() => {
    if (rfi && (isEditMode || isViewMode)) {
      reset({
        subject: rfi.subject,
        question: rfi.question,
        suggestedSolution: rfi.suggestedSolution || '',
        priority: rfi.priority,
        discipline: rfi.discipline,
        dueDate: rfi.dueDate.split('T')[0],
        ballInCourt: typeof rfi.ballInCourt === 'string' ? rfi.ballInCourt : rfi.ballInCourt._id,
        ballInCourtName: rfi.ballInCourtName,
        assignedTo: rfi.assignedTo
          ? typeof rfi.assignedTo === 'string'
            ? rfi.assignedTo
            : rfi.assignedTo._id
          : '',
        assignedToName: rfi.assignedToName || '',
        location: rfi.location || '',
        drawingReference: rfi.drawingReference || '',
        specSection: rfi.specSection || '',
        hasCostImpact: rfi.costImpact?.hasCostImpact || false,
        estimatedCost: rfi.costImpact?.estimatedCost || 0,
        costDescription: rfi.costImpact?.description || '',
        hasScheduleImpact: rfi.scheduleImpact?.hasScheduleImpact || false,
        daysImpacted: rfi.scheduleImpact?.daysImpacted || 0,
        scheduleDescription: rfi.scheduleImpact?.description || '',
      });

      // Expand sections with content
      setExpandedSections({
        basicInfo: true,
        assignments: true,
        references: !!(
          rfi.location ||
          rfi.drawingReference ||
          rfi.specSection ||
          rfi.references?.length
        ),
        impact: !!(rfi.costImpact?.hasCostImpact || rfi.scheduleImpact?.hasScheduleImpact),
        distribution: (rfi.distribution?.length || 0) > 0,
        response: !!rfi.officialResponse,
        comments: (rfi.comments?.length || 0) > 0,
      });
    } else if (isCreateMode) {
      reset({
        subject: '',
        question: '',
        suggestedSolution: '',
        priority: 'medium',
        discipline: 'general',
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        ballInCourt: '',
        ballInCourtName: '',
        assignedTo: '',
        assignedToName: '',
        location: '',
        drawingReference: '',
        specSection: '',
        hasCostImpact: false,
        estimatedCost: 0,
        costDescription: '',
        hasScheduleImpact: false,
        daysImpacted: 0,
        scheduleDescription: '',
      });

      setExpandedSections({
        basicInfo: true,
        assignments: true,
        references: false,
        impact: false,
        distribution: false,
        response: false,
        comments: false,
      });
    }
  }, [rfi, mode, reset, isEditMode, isViewMode, isCreateMode]);

  // Handle form save
  const handleFormSave = async (data: RFIFormData) => {
    if (!projectId) {
      showError('No project selected');
      return;
    }

    try {
      if (isCreateMode) {
        const createData: CreateRFIRequest = {
          subject: data.subject,
          question: data.question,
          suggestedSolution: data.suggestedSolution || undefined,
          priority: data.priority,
          discipline: data.discipline,
          dueDate: data.dueDate,
          ballInCourt: data.ballInCourt,
          ballInCourtName: data.ballInCourtName,
          assignedTo: data.assignedTo || undefined,
          assignedToName: data.assignedToName || undefined,
          location: data.location || undefined,
          drawingReference: data.drawingReference || undefined,
          specSection: data.specSection || undefined,
          costImpact: {
            hasCostImpact: data.hasCostImpact,
            estimatedCost: data.hasCostImpact ? data.estimatedCost : undefined,
            description: data.hasCostImpact ? data.costDescription : undefined,
          },
          scheduleImpact: {
            hasScheduleImpact: data.hasScheduleImpact,
            daysImpacted: data.hasScheduleImpact ? data.daysImpacted : undefined,
            description: data.hasScheduleImpact ? data.scheduleDescription : undefined,
          },
          isDraft: true,
        };

        await createRFI({ projectId, data: createData }).unwrap();
        success('RFI created successfully');
      } else if (isEditMode && rfi) {
        const updateData: UpdateRFIRequest = {
          subject: data.subject,
          question: data.question,
          suggestedSolution: data.suggestedSolution || undefined,
          priority: data.priority,
          discipline: data.discipline,
          dueDate: data.dueDate,
          assignedTo: data.assignedTo || undefined,
          assignedToName: data.assignedToName || undefined,
          location: data.location || undefined,
          drawingReference: data.drawingReference || undefined,
          specSection: data.specSection || undefined,
          costImpact: {
            hasCostImpact: data.hasCostImpact,
            estimatedCost: data.hasCostImpact ? data.estimatedCost : undefined,
            description: data.hasCostImpact ? data.costDescription : undefined,
          },
          scheduleImpact: {
            hasScheduleImpact: data.hasScheduleImpact,
            daysImpacted: data.hasScheduleImpact ? data.daysImpacted : undefined,
            description: data.hasScheduleImpact ? data.scheduleDescription : undefined,
          },
        };

        await updateRFI({ projectId, rfiId: rfi._id, data: updateData }).unwrap();
        success('RFI updated successfully');
      }

      onSuccess?.();
      handleClose();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      showError(error.data?.message || 'Failed to save RFI');
    }
  };

  // Handle submit RFI (draft -> open)
  const handleSubmitRFI = async () => {
    if (!projectId || !rfi) return;

    try {
      await submitRFI({ projectId, rfiId: rfi._id, data: { notifyDistribution: true } }).unwrap();
      success('RFI submitted successfully');
      onSuccess?.();
      handleClose();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      showError(error.data?.message || 'Failed to submit RFI');
    }
  };

  // Handle close RFI
  const handleCloseRFI = async () => {
    if (!projectId || !rfi) return;

    try {
      await closeRFI({
        projectId,
        rfiId: rfi._id,
        data: { reason: closeReason, notifyDistribution: true },
      }).unwrap();
      success('RFI closed successfully');
      setShowCloseModal(false);
      setCloseReason('');
      onSuccess?.();
      handleClose();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      showError(error.data?.message || 'Failed to close RFI');
    }
  };

  // Handle void RFI
  const handleVoidRFI = async () => {
    if (!projectId || !rfi) return;

    try {
      await voidRFI({
        projectId,
        rfiId: rfi._id,
        data: { reason: voidReason, notifyDistribution: true },
      }).unwrap();
      success('RFI voided successfully');
      setShowVoidModal(false);
      setVoidReason('');
      onSuccess?.();
      handleClose();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      showError(error.data?.message || 'Failed to void RFI');
    }
  };

  // Handle PDF download
  const handleDownloadPdf = async () => {
    if (!projectId || !rfi) return;

    try {
      await downloadRFIPdf(projectId, rfi._id, rfi.rfiNumber);
      success('PDF downloaded successfully');
    } catch {
      showError('Failed to download PDF');
    }
  };

  const handleClose = () => {
    if (!isProcessing) onClose();
  };

  // Handle ball-in-court change
  const handleBallInCourtChange = (userId: string) => {
    const member = teamMembers.find(m => m.userId === userId);
    const option = teamMemberOptions.find(o => o.value === userId);
    setValue('ballInCourt', userId);
    setValue(
      'ballInCourtName',
      member?.user
        ? `${member.user.firstName} ${member.user.lastName}`
        : option?.label || userId
    );
  };

  // Handle assignee change
  const handleAssigneeChange = (userId: string) => {
    const member = teamMembers.find(m => m.userId === userId);
    const option = teamMemberOptions.find(o => o.value === userId);
    setValue('assignedTo', userId);
    setValue(
      'assignedToName',
      member?.user
        ? `${member.user.firstName} ${member.user.lastName}`
        : option?.label || ''
    );
  };

  if (!isOpen) return null;

  const canEdit = isCreateMode || (isEditMode && rfi?.status === 'draft');
  const canSubmit = rfi?.status === 'draft';
  const canClose = rfi?.status === 'answered';
  const canVoid = rfi?.status && ['draft', 'open', 'pending_response'].includes(rfi.status);

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-[90]" onClick={handleClose} />

      {/* Drawer - 90% width for view/edit, 60% for create */}
      <div
        className={`fixed top-4 right-4 bottom-4 ${
          isCreateMode ? 'w-[60%]' : 'left-[8%]'
        } z-[100] animate-in slide-in-from-right duration-200`}
      >
        <div className="bg-white rounded-xl shadow-2xl h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold text-gray-900">
                    {isCreateMode ? 'New RFI' : isEditMode ? 'Edit RFI' : rfi?.rfiNumber}
                  </h2>
                  {rfi && (
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[rfi.status]}`}
                    >
                      {STATUS_LABELS[rfi.status]}
                    </span>
                  )}
                  {rfi && (
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${PRIORITY_COLORS[rfi.priority]}`}
                    >
                      {PRIORITY_OPTIONS.find(p => p.value === rfi.priority)?.label}
                    </span>
                  )}
                </div>
                {rfi && !isCreateMode && (
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Due: {formatDate(rfi.dueDate)}
                    </span>
                    {rfi.isOverdue && (
                      <span className="flex items-center gap-1 text-red-500">
                        <Clock className="h-3 w-3" />
                        {rfi.daysOverdue} days overdue
                      </span>
                    )}
                    <span>Ball-in-court: {rfi.ballInCourtName}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* PDF Download */}
              {rfi && (
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Download PDF"
                >
                  <FileDown className="h-4 w-4" />
                </button>
              )}

              {/* Close */}
              <button
                type="button"
                onClick={handleClose}
                disabled={isProcessing}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit(handleFormSave)} className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto">
              <div className="px-6 py-4 space-y-3">
                {/* Basic Info Section */}
                <div className="space-y-2">
                  <SectionHeader
                    icon={<FileText className="h-4 w-4 text-gray-500" />}
                    title="Basic Information"
                    isExpanded={expandedSections.basicInfo}
                    onToggle={() => toggleSection('basicInfo')}
                  />

                  {expandedSections.basicInfo && (
                    <div className="pl-6 space-y-4 pt-2">
                      {/* Subject */}
                      <Controller
                        name="subject"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Subject"
                            placeholder="Enter RFI subject"
                            required
                            error={errors.subject?.message}
                            disabled={!canEdit}
                            {...field}
                          />
                        )}
                      />

                      {/* Question */}
                      <Controller
                        name="question"
                        control={control}
                        render={({ field }) => (
                          <TextArea
                            label="Question"
                            placeholder="Enter your question or request for information"
                            required
                            rows={4}
                            error={errors.question?.message}
                            disabled={!canEdit}
                            {...field}
                          />
                        )}
                      />

                      {/* Suggested Solution */}
                      <Controller
                        name="suggestedSolution"
                        control={control}
                        render={({ field }) => (
                          <TextArea
                            label="Suggested Solution"
                            placeholder="Enter suggested solution (optional)"
                            rows={3}
                            error={errors.suggestedSolution?.message}
                            disabled={!canEdit}
                            {...field}
                          />
                        )}
                      />

                      {/* Priority & Discipline */}
                      <div className="grid grid-cols-2 gap-4">
                        <Controller
                          name="priority"
                          control={control}
                          render={({ field }) => (
                            <Dropdown
                              label="Priority"
                              options={PRIORITY_OPTIONS}
                              value={field.value}
                              onChange={field.onChange}
                              required
                              disabled={!canEdit}
                            />
                          )}
                        />

                        <Controller
                          name="discipline"
                          control={control}
                          render={({ field }) => (
                            <Dropdown
                              label="Discipline"
                              options={DISCIPLINE_OPTIONS}
                              value={field.value}
                              onChange={field.onChange}
                              required
                              disabled={!canEdit}
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Assignments Section */}
                <div className="space-y-2">
                  <SectionHeader
                    icon={<Users className="h-4 w-4 text-gray-500" />}
                    title="Assignments"
                    isExpanded={expandedSections.assignments}
                    onToggle={() => toggleSection('assignments')}
                  />

                  {expandedSections.assignments && (
                    <div className="pl-6 space-y-4 pt-2">
                      <div className="grid grid-cols-2 gap-4">
                        {/* Ball-in-court */}
                        <Controller
                          name="ballInCourt"
                          control={control}
                          render={({ field }) => (
                            <SearchableDropdown
                              label="Ball-in-Court"
                              placeholder="Select responsible party"
                              options={teamMemberOptions}
                              value={field.value}
                              onChange={handleBallInCourtChange}
                              required
                              disabled={!canEdit}
                              error={errors.ballInCourt?.message}
                            />
                          )}
                        />

                        {/* Assigned To */}
                        <Controller
                          name="assignedTo"
                          control={control}
                          render={({ field }) => (
                            <SearchableDropdown
                              label="Assigned To"
                              placeholder="Select assignee (optional)"
                              options={teamMemberOptions}
                              value={field.value}
                              onChange={handleAssigneeChange}
                              disabled={!canEdit}
                            />
                          )}
                        />
                      </div>

                      {/* Due Date */}
                      <Controller
                        name="dueDate"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            label="Due Date"
                            value={field.value}
                            onChange={field.onChange}
                            required
                            disabled={!canEdit}
                            error={errors.dueDate?.message}
                          />
                        )}
                      />
                    </div>
                  )}
                </div>

                {/* References Section */}
                <div className="space-y-2">
                  <SectionHeader
                    icon={<FileText className="h-4 w-4 text-gray-500" />}
                    title="References & Location"
                    count={rfi?.references?.length}
                    isExpanded={expandedSections.references}
                    onToggle={() => toggleSection('references')}
                  />

                  {expandedSections.references && (
                    <div className="pl-6 space-y-4 pt-2">
                      <div className="grid grid-cols-2 gap-4">
                        <Controller
                          name="location"
                          control={control}
                          render={({ field }) => (
                            <Input
                              label="Location"
                              placeholder="e.g., Building A, Floor 2"
                              disabled={!canEdit}
                              {...field}
                            />
                          )}
                        />

                        <Controller
                          name="drawingReference"
                          control={control}
                          render={({ field }) => (
                            <Input
                              label="Drawing Reference"
                              placeholder="e.g., A-101, S-201"
                              disabled={!canEdit}
                              {...field}
                            />
                          )}
                        />
                      </div>

                      <Controller
                        name="specSection"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Specification Section"
                            placeholder="e.g., 03 30 00 - Cast-in-Place Concrete"
                            disabled={!canEdit}
                            {...field}
                          />
                        )}
                      />

                      {/* References List (view mode) */}
                      {rfi && !isCreateMode && (
                        <RFIReferencesSection
                          projectId={projectId || ''}
                          rfi={rfi}
                          canEdit={canEdit}
                          onSuccess={onSuccess}
                        />
                      )}
                    </div>
                  )}
                </div>

                {/* Impact Assessment Section */}
                <div className="space-y-2">
                  <SectionHeader
                    icon={<AlertCircle className="h-4 w-4 text-gray-500" />}
                    title="Impact Assessment"
                    isExpanded={expandedSections.impact}
                    onToggle={() => toggleSection('impact')}
                    badge={
                      (hasCostImpact || hasScheduleImpact) && (
                        <span className="text-xs text-orange-600 font-medium">Has Impact</span>
                      )
                    }
                  />

                  {expandedSections.impact && (
                    <div className="pl-6 space-y-4 pt-2">
                      {/* Cost Impact */}
                      <div className="p-3 bg-gray-50 rounded-lg space-y-3">
                        <Controller
                          name="hasCostImpact"
                          control={control}
                          render={({ field }) => (
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={field.value}
                                onChange={e => field.onChange(e.target.checked)}
                                disabled={!canEdit}
                                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                              />
                              <span className="text-sm font-medium text-gray-700">
                                Has Cost Impact
                              </span>
                            </label>
                          )}
                        />

                        {hasCostImpact && (
                          <div className="grid grid-cols-2 gap-4">
                            <Controller
                              name="estimatedCost"
                              control={control}
                              render={({ field }) => (
                                <CurrencyInput
                                  label="Estimated Cost"
                                  value={field.value}
                                  onChange={field.onChange}
                                  disabled={!canEdit}
                                />
                              )}
                            />

                            <Controller
                              name="costDescription"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  label="Description"
                                  placeholder="Describe cost impact"
                                  disabled={!canEdit}
                                  {...field}
                                />
                              )}
                            />
                          </div>
                        )}
                      </div>

                      {/* Schedule Impact */}
                      <div className="p-3 bg-gray-50 rounded-lg space-y-3">
                        <Controller
                          name="hasScheduleImpact"
                          control={control}
                          render={({ field }) => (
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={field.value}
                                onChange={e => field.onChange(e.target.checked)}
                                disabled={!canEdit}
                                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                              />
                              <span className="text-sm font-medium text-gray-700">
                                Has Schedule Impact
                              </span>
                            </label>
                          )}
                        />

                        {hasScheduleImpact && (
                          <div className="grid grid-cols-2 gap-4">
                            <Controller
                              name="daysImpacted"
                              control={control}
                              render={({ field }) => (
                                <NumberInput
                                  label="Days Impacted"
                                  value={field.value}
                                  onChange={field.onChange}
                                  min={0}
                                  disabled={!canEdit}
                                />
                              )}
                            />

                            <Controller
                              name="scheduleDescription"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  label="Description"
                                  placeholder="Describe schedule impact"
                                  disabled={!canEdit}
                                  {...field}
                                />
                              )}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Distribution Section (view/edit only) */}
                {rfi && !isCreateMode && (
                  <div className="space-y-2">
                    <SectionHeader
                      icon={<Users className="h-4 w-4 text-gray-500" />}
                      title="Distribution List"
                      count={rfi.distribution?.length}
                      isExpanded={expandedSections.distribution}
                      onToggle={() => toggleSection('distribution')}
                    />

                    {expandedSections.distribution && (
                      <div className="pl-6 pt-2">
                        <RFIDistributionSection
                          projectId={projectId || ''}
                          rfi={rfi}
                          teamMembers={teamMembers}
                          canEdit={canEdit}
                          onSuccess={onSuccess}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Response Section (view only when responded) */}
                {rfi && !isCreateMode && (
                  <div className="space-y-2">
                    <SectionHeader
                      icon={<CheckCircle className="h-4 w-4 text-gray-500" />}
                      title="Official Response"
                      isExpanded={expandedSections.response}
                      onToggle={() => toggleSection('response')}
                      badge={
                        rfi.officialResponse ? (
                          <span className="text-xs text-green-600 font-medium">Answered</span>
                        ) : (
                          <span className="text-xs text-gray-400">Pending</span>
                        )
                      }
                    />

                    {expandedSections.response && (
                      <div className="pl-6 pt-2">
                        <RFIResponseSection
                          projectId={projectId || ''}
                          rfi={rfi}
                          canRespond={rfi.status === 'open' || rfi.status === 'pending_response'}
                          onSuccess={onSuccess}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Comments Section (view only) */}
                {rfi && !isCreateMode && (
                  <div className="space-y-2">
                    <SectionHeader
                      icon={<MessageSquare className="h-4 w-4 text-gray-500" />}
                      title="Comments"
                      count={rfi.comments?.length}
                      isExpanded={expandedSections.comments}
                      onToggle={() => toggleSection('comments')}
                    />

                    {expandedSections.comments && (
                      <div className="pl-6 pt-2">
                        <RFICommentsSection
                          projectId={projectId || ''}
                          rfi={rfi}
                          onSuccess={onSuccess}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-100 px-6 py-3 bg-gray-50/50">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-400">
                  {rfi && `Updated ${formatDate(rfi.updatedAt)}`}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isProcessing}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {isViewMode ? 'Close' : 'Cancel'}
                  </button>

                  {/* Void button */}
                  {isViewMode && canVoid && (
                    <button
                      type="button"
                      onClick={() => setShowVoidModal(true)}
                      disabled={isProcessing}
                      className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1"
                    >
                      <XCircle className="h-3.5 w-3.5" />
                      <span>Void</span>
                    </button>
                  )}

                  {/* Close RFI button */}
                  {isViewMode && canClose && (
                    <button
                      type="button"
                      onClick={() => setShowCloseModal(true)}
                      disabled={isProcessing}
                      className="px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors flex items-center gap-1"
                    >
                      <CheckCircle className="h-3.5 w-3.5" />
                      <span>Close RFI</span>
                    </button>
                  )}

                  {/* Submit button (for drafts) */}
                  {isViewMode && canSubmit && (
                    <button
                      type="button"
                      onClick={handleSubmitRFI}
                      disabled={isProcessing}
                      className="px-4 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <Send className="h-3.5 w-3.5" />
                          <span>Submit RFI</span>
                        </>
                      )}
                    </button>
                  )}

                  {/* Save button (for create/edit) */}
                  {canEdit && (
                    <button
                      type="submit"
                      disabled={isProcessing}
                      className="px-4 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                    >
                      {isSaving ? (
                        <>
                          <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <span>{isCreateMode ? 'Create RFI' : 'Save'}</span>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Close RFI Modal */}
      {showCloseModal &&
        createPortal(
          <>
            <div
              className="fixed inset-0 bg-black/50 z-[150]"
              onClick={() => setShowCloseModal(false)}
            />
            <div className="fixed inset-0 z-[151] flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-5">
                <h3 className="text-base font-semibold text-gray-900 mb-3">Close RFI</h3>
                <p className="text-sm text-gray-500 mb-3">
                  Closing this RFI will mark it as resolved. Add any closing notes below.
                </p>
                <textarea
                  value={closeReason}
                  onChange={e => setCloseReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="Closing notes (optional)..."
                />
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCloseModal(false);
                      setCloseReason('');
                    }}
                    disabled={isClosing}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseRFI}
                    disabled={isClosing}
                    className="px-4 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {isClosing ? 'Closing...' : 'Close RFI'}
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}

      {/* Void RFI Modal */}
      {showVoidModal &&
        createPortal(
          <>
            <div
              className="fixed inset-0 bg-black/50 z-[150]"
              onClick={() => setShowVoidModal(false)}
            />
            <div className="fixed inset-0 z-[151] flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-5">
                <h3 className="text-base font-semibold text-gray-900 mb-3">Void RFI</h3>
                <p className="text-sm text-gray-500 mb-3">
                  Voiding this RFI will mark it as cancelled. This action cannot be undone.
                </p>
                <textarea
                  value={voidReason}
                  onChange={e => setVoidReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="Reason for voiding (required)..."
                />
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowVoidModal(false);
                      setVoidReason('');
                    }}
                    disabled={isVoiding}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleVoidRFI}
                    disabled={!voidReason.trim() || isVoiding}
                    className="px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {isVoiding ? 'Voiding...' : 'Void RFI'}
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
    </>,
    document.body
  );
};

export default RFIDrawer;
